import { PlanLabelPdfMetrics } from '@editor/hooks/usePlanLabelPdfMetrics'
import { ElementLabelDescriptor } from 'src/components/scene/ModelElementLabels/types'
import { calculateTextWidth } from 'src/components/scene/TextSprite/utils'

/**
 * Geometry of the PDF plan label boxes (`make_label_V2` / `labelBoxSize` in the backend's
 * `functions/export/plans/pdf/pypdf.py`, with the box rule of the viewer annotation makers in
 * `pdf/bluebeam.py`, which every target viewer shares), converted to world units. The
 * floorplan labels use these so they render at the size the exported plan
 * will have, letting the engineer judge placement in the scene.
 */

/** `LABEL_LINE_SPACING_RATIO` — the PDF label's single-sided line gap in em (lines are 1.3 em apart) */
export const PDF_LABEL_LINE_GAP_SINGLE_SIDED = 0.15

/** `1 + 2 * LABEL_LINE_SPACING_RATIO` — the PDF label line height in em */
export const PDF_LABEL_LINE_HEIGHT = 1 + 2 * PDF_LABEL_LINE_GAP_SINGLE_SIDED

/**
 * `BLUEBEAM_FIRST_LINE_MIN_EXTRA_PT` — the first line of a PDF label is at least the font size
 * plus this much high, so labels below 10 pt get a taller box than 1.3 em per line
 */
const PDF_FIRST_LINE_MIN_EXTRA_PT = 3

/** `BLUEBEAM_MIN_FREE_WIDTH_PT` — free width a PDF label box leaves at least beside its widest line */
const PDF_MIN_FREE_WIDTH_PT = 3.2

/** inner padding of a PDF label box, in points */
const PDF_LABEL_MARGIN_PT = 1

/** border stroke width of a bordered PDF label box, in points */
const PDF_LABEL_BORDER_WIDTH_PT = 0.5

/** `ADDITIONAL_SCALING_NUM_CHARACTERS_THRESHOLD` — lines this short get the wider box */
const PDF_SHORT_LABEL_MAX_CHARS = 8

/** `PDFPlanSettings.maxTextWidthScalingFactor` default the element label PDF writers pass explicitly */
export const PDF_TEXT_WIDTH_SCALE = 1.1

/**
 * `make_label_V2`'s own default `maxTextWidthScalingFactor`. The PDF's
 * `comment()` writer calls `make_label_V2` without overriding it, so scene
 * comments must scale by this instead of `PDF_TEXT_WIDTH_SCALE`.
 */
export const PDF_COMMENT_TEXT_WIDTH_SCALE = 1.15

/** width scaling of short or borderless labels (hardcoded in `make_label_V2`) */
const PDF_TEXT_WIDTH_SCALE_SHORT = 1.5

/** `calculate_text_width_arial`'s width for characters outside its glyph table, in em */
const PDF_UNKNOWN_GLYPH_WIDTH = 0.5

/**
 * Padding between the text block and the box edge (margin + border) in world
 * units. Wall labels are drawn without a border in the PDF (`hasBorder` false).
 */
export const getPdfLabelPaddingWorld = (metrics: PlanLabelPdfMetrics, hasBorder = true): number =>
  (PDF_LABEL_MARGIN_PT + (hasBorder ? PDF_LABEL_BORDER_WIDTH_PT : 0)) * metrics.ptToWorld

/**
 * Height of the PDF label's text block in world units (`bluebeamTextBlockHeight`):
 * `PDF_LABEL_LINE_HEIGHT` per line, the first line at least the font size plus
 * `PDF_FIRST_LINE_MIN_EXTRA_PT`. No lines, no block.
 */
export const getPdfTextBlockHeightWorld = (
  metrics: PlanLabelPdfMetrics,
  numLines: number,
): number => {
  if (numLines <= 0) return 0
  const lineHeight = PDF_LABEL_LINE_HEIGHT * metrics.emWorld
  const firstLine = Math.max(
    lineHeight,
    metrics.emWorld + PDF_FIRST_LINE_MIN_EXTRA_PT * metrics.ptToWorld,
  )
  return firstLine + (numLines - 1) * lineHeight
}

/**
 * Size of the text a PDF label box wraps, in world units: the widest line's width,
 * unscaled, and the text block height. The export never measures its text: the
 * width comes from `approx_text_width_arial`, a per-glyph Arial width table. The
 * scene labels size their boxes from this rather than from their own typeset text
 * (Roboto, measured by troika), so the boxes come out exactly as wide and as tall
 * as the exported ones.
 */
export const getPdfTextBlockSizeWorld = (
  textLines: string[],
  metrics: PlanLabelPdfMetrics,
): { width: number; height: number } => ({
  width: Math.max(
    0,
    ...textLines.map(line => calculateTextWidth(line, metrics.emWorld, PDF_UNKNOWN_GLYPH_WIDTH)),
  ),
  height: getPdfTextBlockHeightWorld(metrics, textLines.length),
})

/**
 * Full height of the rendered label box in world units — used to offset an
 * edge-anchored PDF label into the center-anchored scene position. Without
 * lines (`numLines` 0) only the padding is left.
 */
export const getPdfLabelBoxHeightWorld = (
  metrics: PlanLabelPdfMetrics,
  hasBorder = true,
  numLines = 1,
): number =>
  getPdfTextBlockHeightWorld(metrics, numLines) + 2 * getPdfLabelPaddingWorld(metrics, hasBorder)

/** Extra width factor the PDF gives a label's text block. */
export const getPdfTextWidthScale = (
  textLines: string[],
  hasBorder = true,
  boxScale = PDF_TEXT_WIDTH_SCALE,
): number => {
  const maxLineLength = Math.max(...textLines.map(line => line.length), 0)
  return !hasBorder || maxLineLength <= PDF_SHORT_LABEL_MAX_CHARS
    ? PDF_TEXT_WIDTH_SCALE_SHORT
    : boxScale
}

/**
 * Width of the PDF label's text block in world units (`_labelTextBlockWidth`):
 * the widest line scaled by `getPdfTextWidthScale`, but at least
 * `PDF_MIN_FREE_WIDTH_PT` wider than the line.
 */
export const getPdfTextBlockWidthWorld = (
  textLines: string[],
  metrics: PlanLabelPdfMetrics,
  hasBorder = true,
  boxScale = PDF_TEXT_WIDTH_SCALE,
): number => {
  const { width } = getPdfTextBlockSizeWorld(textLines, metrics)
  return Math.max(
    width * getPdfTextWidthScale(textLines, hasBorder, boxScale),
    width + PDF_MIN_FREE_WIDTH_PT * metrics.ptToWorld,
  )
}

/**
 * Width and height of the PDF label box in world units, unrotated, padding
 * included (`labelBoxSize`).
 */
export const getPdfLabelBoxSizeWorld = (
  textLines: string[],
  metrics: PlanLabelPdfMetrics,
  hasBorder = true,
  boxScale = PDF_TEXT_WIDTH_SCALE,
): { width: number; height: number } => {
  const padding = getPdfLabelPaddingWorld(metrics, hasBorder)
  return {
    width: getPdfTextBlockWidthWorld(textLines, metrics, hasBorder, boxScale) + 2 * padding,
    height: getPdfLabelBoxHeightWorld(metrics, hasBorder, textLines.length),
  }
}

/**
 * The descriptor fields that give a scene label the exact box of its PDF
 * counterpart (`make_label_V2`): em size, line height, box padding and the
 * (already scaled) text block the box wraps.
 */
export const getPdfLabelBoxDescriptorProps = (
  textLines: string[],
  metrics: PlanLabelPdfMetrics,
  hasBorder = true,
): Pick<
  ElementLabelDescriptor,
  'fontSize' | 'lineHeight' | 'backgroundPaddingWorld' | 'backgroundTextBlockSize'
> => ({
  fontSize: metrics.emWorld,
  lineHeight: PDF_LABEL_LINE_HEIGHT,
  backgroundPaddingWorld: getPdfLabelPaddingWorld(metrics, hasBorder),
  backgroundTextBlockSize: {
    width: getPdfTextBlockWidthWorld(textLines, metrics, hasBorder),
    height: getPdfTextBlockHeightWorld(metrics, textLines.length),
  },
})
